import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// Referral letter templates (clinical lead, 28/10/2026). A template sets how
// a clinician's referral letters look (layout, sections, tone); it never
// holds patient data. Built-in templates are listed by key; a clinician's own
// templates by public id. Kept in step with @rosc/processing
// REFERRAL_TEMPLATE_LIMITS and REFERRAL_BUILT_IN_KEYS.
export const REFERRAL_TEMPLATE_MAX = 50;
export const REFERRAL_TEMPLATE_NAME_MAX = 100;
export const REFERRAL_SPECIALTY_MAX = 120;
export const REFERRAL_SECTION_MAX = 100;
export const REFERRAL_SECTIONS_MAX = 20;
export const REFERRAL_INSTRUCTIONS_MAX = 4000;
export const REFERRAL_LAYOUT_MAX = 6000;
export const REFERRAL_RECIPIENT_MAX = 200;
export const REFERRAL_REASON_MAX = 2000;
// Example letters: the file is read once and never stored.
export const REFERRAL_EXAMPLE_MAX_BYTES = 10485760;
export const REFERRAL_EXAMPLE_TEXT_MAX = 40000;
export const REFERRAL_EXAMPLE_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;

export const REFERRAL_BUILT_IN_KEYS = [
  "consult-request",
  "brief-letter",
] as const;
export const referralBuiltInKeySchema = z.enum(REFERRAL_BUILT_IN_KEYS);
export type ReferralBuiltInKey = z.output<typeof referralBuiltInKeySchema>;
export const REFERRAL_URGENCIES = ["routine", "soon", "urgent"] as const;
export const referralUrgencySchema = z.enum(REFERRAL_URGENCIES);
export type ReferralUrgency = z.output<typeof referralUrgencySchema>;

const chars = (max: number) => (value: string) =>
  Array.from(value).length <= max;
const line = (max: number) =>
  z
    .string()
    .transform((value) => value.normalize("NFC").replace(/\s+/g, " ").trim())
    .pipe(z.string().min(1).refine(chars(max), `At most ${max} characters`));
const block = (max: number) =>
  z
    .string()
    .transform((value) => value.normalize("NFC").replace(/\r\n?/g, "\n").trim())
    .pipe(z.string().refine(chars(max), `At most ${max} characters`));

export const referralTemplateNameSchema = line(REFERRAL_TEMPLATE_NAME_MAX);
export const referralSpecialtySchema = line(REFERRAL_SPECIALTY_MAX);
export const referralSectionsSchema = z
  .array(line(REFERRAL_SECTION_MAX))
  .max(REFERRAL_SECTIONS_MAX);

export const referralTemplateDefinitionSchema = z
  .strictObject({
    name: referralTemplateNameSchema,
    specialty: referralSpecialtySchema.nullable().default(null),
    sections: referralSectionsSchema.default([]),
    instructions: block(REFERRAL_INSTRUCTIONS_MAX).default(""),
    layout: block(REFERRAL_LAYOUT_MAX).default(""),
    isDefault: z.boolean().default(false),
    // "example": made from an uploaded or pasted example letter.
    source: z.enum(["manual", "example", "built-in"]).default("manual"),
  })
  .refine(
    (value) => value.instructions.length > 0 || value.layout.length > 0,
    "A template needs a layout or instructions",
  );
export type ReferralTemplateDefinition = z.output<
  typeof referralTemplateDefinitionSchema
>;
export const referralTemplateUpdateSchema = z
  .strictObject({
    name: referralTemplateNameSchema.optional(),
    specialty: referralSpecialtySchema.nullable().optional(),
    sections: referralSectionsSchema.optional(),
    instructions: block(REFERRAL_INSTRUCTIONS_MAX).optional(),
    layout: block(REFERRAL_LAYOUT_MAX).optional(),
    isDefault: z.boolean().optional(),
  })
  .refine(
    (value) => Object.values(value).some((field) => field !== undefined),
    "An edit is required",
  );
export type ReferralTemplateUpdate = z.output<
  typeof referralTemplateUpdateSchema
>;

const content = {
  name: z.string().min(1),
  specialty: z.string().nullable(),
  sections: z.array(z.string()),
  instructions: z.string(),
  layout: z.string(),
};
export const referralTemplateSchema = z.object({
  public_id: publicIdSchema,
  ...content,
  isDefault: z.boolean(),
  source: z.enum(["manual", "example", "built-in"]),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ReferralTemplate = z.output<typeof referralTemplateSchema>;
export const referralBuiltInTemplateSchema = z.object({
  key: referralBuiltInKeySchema,
  ...content,
});
export type ReferralBuiltInTemplate = z.output<
  typeof referralBuiltInTemplateSchema
>;
export const referralTemplateListSchema = z.object({
  items: z.array(referralTemplateSchema).max(REFERRAL_TEMPLATE_MAX),
  builtIns: z.array(referralBuiltInTemplateSchema),
});

const base64 = (maxBytes: number) =>
  z
    .string()
    .min(4)
    .max(Math.ceil(maxBytes / 3) * 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Expected base64");
export const referralExampleSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("text"),
    text: z
      .string()
      .trim()
      .min(40, "Paste the whole letter")
      .refine(chars(REFERRAL_EXAMPLE_TEXT_MAX), "Letter is too long"),
  }),
  z.strictObject({
    kind: z.literal("file"),
    mediaType: z.enum(REFERRAL_EXAMPLE_MEDIA_TYPES),
    data: base64(REFERRAL_EXAMPLE_MAX_BYTES),
  }),
]);
export type ReferralExample = z.output<typeof referralExampleSchema>;
export const referralTemplateExtractionInputSchema = z.strictObject({
  example: referralExampleSchema,
});
export const referralTemplateDraftSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(REFERRAL_TEMPLATE_NAME_MAX * 4),
  specialty: z
    .string()
    .max(REFERRAL_SPECIALTY_MAX * 4)
    .nullable(),
  sections: z
    .array(
      z
        .string()
        .min(1)
        .max(REFERRAL_SECTION_MAX * 4),
    )
    .max(REFERRAL_SECTIONS_MAX),
  instructions: z.string().max(REFERRAL_INSTRUCTIONS_MAX * 4),
  layout: z.string().max(REFERRAL_LAYOUT_MAX * 4),
});
export type ReferralTemplateDraft = z.output<
  typeof referralTemplateDraftSchema
>;
// Nothing is saved: the clinician reviews the draft and saves it with
// createReferralTemplate (source "example").
export const referralTemplateExtractionSchema = z.object({
  draft: referralTemplateDraftSchema,
  // Patient identifiers were found and replaced with placeholders.
  identifiersRemoved: z.boolean(),
  summary: z.string(),
});
export type ReferralTemplateExtraction = z.output<
  typeof referralTemplateExtractionSchema
>;

// What the clinician chose in the referral dialog; sent with a "Referral
// Letter" note generation. letterTemplate is a built-in key or the public id of
// one of the clinician's templates (default: the clinician's default
// template, then the first built-in).
export const referralLetterOptionsSchema = z.strictObject({
  specialty: referralSpecialtySchema,
  recipient: line(REFERRAL_RECIPIENT_MAX).optional(),
  // Deprecated (28/10/2026): no longer asked for or used in the letter.
  // Still accepted so older web builds keep working.
  urgency: referralUrgencySchema.optional(),
  // The clinician's instructions ("Instructions" in the web dialog): the
  // reason for referral and anything else they want in the letter.
  reason: block(REFERRAL_REASON_MAX).optional(),
  letterTemplate: z
    .union([referralBuiltInKeySchema, publicIdSchema])
    .optional(),
});
export type ReferralLetterOptions = z.output<
  typeof referralLetterOptionsSchema
>;

const errors = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Referral template not found" },
  CONFLICT: { message: "Referral template conflict" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Referral templates unavailable" },
});
const query = z.strictObject({}).default({});
const params = z.strictObject({ public_id: publicIdSchema });
const meta = (
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  successStatus = 200,
) =>
  openapi({
    method,
    path: `/api/v2${path}`,
    inputStructure: "detailed",
    successStatus,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });

export const referralTemplateContract = {
  listReferralTemplates: errors
    .input(z.object({ query, body: z.undefined().optional() }))
    .meta(meta("GET", "/referral-templates"))
    .output(referralTemplateListSchema),
  createReferralTemplate: errors
    .input(
      z.object({
        query,
        body: z.union([
          referralTemplateDefinitionSchema,
          // A copy of a built-in template, to change it.
          z.strictObject({ builtInKey: referralBuiltInKeySchema }),
        ]),
      }),
    )
    .meta(meta("POST", "/referral-templates", 201))
    .output(referralTemplateSchema),
  updateReferralTemplate: errors
    .input(z.object({ params, query, body: referralTemplateUpdateSchema }))
    .meta(meta("PATCH", "/referral-templates/{public_id}"))
    .output(referralTemplateSchema),
  deleteReferralTemplate: errors
    .input(z.object({ params, query, body: z.undefined().optional() }))
    .meta(meta("DELETE", "/referral-templates/{public_id}"))
    .output(z.object({ public_id: publicIdSchema })),
  createReferralTemplateExtraction: errors
    .input(z.object({ query, body: referralTemplateExtractionInputSchema }))
    .meta(meta("POST", "/referral-template-extractions"))
    .output(referralTemplateExtractionSchema),
};
